import React from 'react';

import { getLocalFormatedTime } from '@lumapps/utils/time';
import { getMoment } from '@lumapps/utils/time/getMoment';
import { Time } from '@lumapps/utils/types/Time';

interface UseTimePickerFieldProps {
    /** List of time displayed */
    timeList: Time[];
    /** Returned if the searched time is before min */
    minTime?: Time;
    /** Locale (language or region) to use. */
    locale?: string;
}

/** regex for matching rounded 24 hours time input */
const regex24Time = /^([0-1]?[\d]|2[0-3])$/;
/** regex for matching rounded 12 hours time input */
const regex12Time = /^([1-9]|1[0-2])\s*([ap]?|[ap]m?)$/i;
/** regex for matching 24 hours time input with minutes */
const regex24MinTime = /^([0-1]?[\d]|2[0-3]):?([0-5]\d)$/;
/** regex for matching 12 hours time input with minutes */
const regex12MinTime = /^([1-9]|1[0-2]):?([0-5]\d)\s*([ap]?|[ap]m?)$/i;

const TIME_FORMATS = [
    { regex: regex24Time, format: 'h' },
    { regex: regex12Time, format: 'ha' },
    { regex: regex24MinTime, format: 'hmm' },
    { regex: regex12MinTime, format: 'hmma' },
];

const moment = getMoment();

export const useTimePickerField = ({ timeList, minTime, locale }: UseTimePickerFieldProps) => {
    /** format typed time to localy formated time  */
    const getTimeFromCleanedValue = React.useCallback(
        (cleanedValue: string, inputFormat: string) => {
            const hour = moment(cleanedValue, inputFormat).hour();
            const minute = moment(cleanedValue, inputFormat).minute();

            if (Number.isNaN(hour) || Number.isNaN(minute)) {
                return undefined;
            }

            /** Return the closest rounded up Time in timeList if the searched time is before min */
            if (minTime && locale) {
                const currentTime = moment(cleanedValue, 'h:mma');
                const formattedMinTime = moment(getLocalFormatedTime(minTime, locale), 'h:mma');

                if (currentTime.isBefore(formattedMinTime)) {
                    const closestMinTime = timeList.find((value) => {
                        const formattedTimeListValue = moment(getLocalFormatedTime(value, locale), 'h:mma');
                        return (
                            formattedMinTime.isBefore(formattedTimeListValue) ||
                            formattedMinTime.isSame(formattedTimeListValue, 'second')
                        );
                    });
                    return closestMinTime;
                }
            }

            return { hour, minute };
        },
        [locale, minTime, timeList],
    );

    /** check if typed value can be time formated  */
    const formatTimeValue = React.useCallback(
        (value: string) => {
            /** remove whitespaces and colon */
            const cleanedValue = value.trim().replace(/:/g, '');

            /** Try to find a matching time format with the regex
             * If one is found, apply the correct formating to the value
             * to automaticaly update the current field
             */
            for (const { regex, format } of TIME_FORMATS) {
                if (value.match(regex)) {
                    return getTimeFromCleanedValue(cleanedValue, format);
                }
            }

            /** If the value is already valid, format it */
            if (moment(value).isValid()) {
                return getTimeFromCleanedValue(cleanedValue, 'hmma');
            }
            return undefined;
        },
        [getTimeFromCleanedValue],
    );

    return {
        /** Get a formated Time value from a user input */
        formatTimeValue,
    };
};
